/**
 * Farbige Auszeichnung aus dem Terminal — Segmente statt Escapes.
 *
 * Codex färbt viel und formatiert wenig: Dateinamen, Befehle, Diff-Zeilen,
 * Fehler. Im Portal kam davon nichts an, deshalb wirkte reiner Codex-Text leer
 * (Alex). Cloud liefert die Auszeichnung jetzt als Bereichsliste neben dem
 * Text (`spans`), statt Escapes in den Text zu mischen — damit bleibt `content`
 * unverändert lesbar für Sprachausgabe, Suche und jeden anderen Verbraucher.
 *
 * Diese Datei macht daraus eine Segmentfolge. Bewusst pur und ohne React:
 * Die Offset-Arithmetik ist die einzige Stelle, an der etwas falsch werden
 * kann, und sie soll prüfbar sein.
 */

export type SpanRole =
  | 'error' | 'success' | 'warning' | 'path' | 'command'
  | 'diff_add' | 'diff_del' | 'muted';

export interface TextSpan {
  start: number;
  end: number;
  role?: string;
  color?: string;
  bold?: boolean;
}

export interface Segment {
  text: string;
  role?: string;
  color?: string;
  bold?: boolean;
}

/**
 * Zerlegt `text` anhand der Bereiche in Segmente. Unmarkierte Lücken bleiben
 * als Segmente ohne Rolle erhalten, damit nichts verlorengeht.
 *
 * FAIL CLOSED an jeder Stelle, an der ein Bereich nicht zum Text passt:
 * Ein Bereich außerhalb der Länge, mit end <= start, oder einer, der einen
 * vorherigen überlappt, wird ÜBERSPRUNGEN — nicht zurechtgebogen. Ein falsch
 * eingefärbtes Stück Text ist schlimmer als ein nicht eingefärbtes, weil es
 * Bedeutung behauptet (rot = Fehler), die es nicht gibt.
 */
export function segmentBySpans(text: string, spans?: TextSpan[] | null): Segment[] {
  if (!text) return [];
  if (!spans || spans.length === 0) return [{ text }];

  const sorted = [...spans]
    .filter((s) => Number.isInteger(s.start) && Number.isInteger(s.end))
    .filter((s) => s.start >= 0 && s.end > s.start && s.end <= text.length)
    .sort((a, b) => a.start - b.start);

  const out: Segment[] = [];
  let cursor = 0;
  for (const s of sorted) {
    if (s.start < cursor) continue;                 // Überlappung → auslassen
    if (s.start > cursor) out.push({ text: text.slice(cursor, s.start) });
    out.push({
      text: text.slice(s.start, s.end),
      ...(s.role ? { role: s.role } : {}),
      ...(s.color ? { color: s.color } : {}),
      ...(s.bold ? { bold: true } : {}),
    });
    cursor = s.end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor) });
  return out;
}

/**
 * Rolle → CSS-Klasse. Über die ROLLE, nicht über die Farbe: Ein hartes
 * Terminal-Rot ist im hellen Thema unlesbar und im dunklen zu grell; die Rolle
 * lässt das Portal seine eigenen Tokens wählen und in beiden Themen stimmen.
 *
 * Kennt Cloud die Rolle nicht, kommt nur `color` — dann bilde ich die acht
 * Terminalnamen auf dieselben Klassen ab. Alles Unbekannte bleibt ohne Klasse:
 * lieber neutral als bunt geraten.
 */
const ROLE_CLASS: Record<string, string> = {
  error: 'sp-error',
  success: 'sp-success',
  warning: 'sp-warning',
  path: 'sp-path',
  command: 'sp-command',
  diff_add: 'sp-add',
  diff_del: 'sp-del',
  muted: 'sp-muted',
};

const COLOR_CLASS: Record<string, string> = {
  red: 'sp-error',
  green: 'sp-success',
  yellow: 'sp-warning',
  cyan: 'sp-path',
  magenta: 'sp-command',
  blue: 'sp-path',
  white: '',
  black: 'sp-muted',
};

export function segmentClass(seg: Segment): string {
  const byRole = seg.role ? ROLE_CLASS[seg.role] : undefined;
  const byColor = !byRole && seg.color ? COLOR_CLASS[seg.color.toLowerCase()] : undefined;
  const base = byRole ?? byColor ?? '';
  return [base, seg.bold ? 'sp-bold' : ''].filter(Boolean).join(' ');
}
